import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

/**
 * Handles submitting the product add-to-cart form via the Ajax Cart API,
 * updating the cart drawer/items sections, and opening the cart drawer.
 *
 * @extends {Component}
 */
class ProductFormComponent extends Component {
  get productId() {
    return this.dataset.productId;
  }

  /**
   * @param {SubmitEvent} event
   */
  handleSubmit(event) {
    event.preventDefault();

    const form = this.querySelector('form');
    if (!(form instanceof HTMLFormElement)) return;

    const submitButton = event.submitter instanceof HTMLElement ? event.submitter : form.querySelector('[type="submit"]');
    if (submitButton?.getAttribute('aria-disabled') === 'true') return;
    const isBuyNow = submitButton?.dataset.buyNow === 'true';

    this.#clearError();
    submitButton?.setAttribute('aria-disabled', 'true');
    submitButton?.classList.add('loading');

    const formData = new FormData(form);
    const sections = this.#sectionsToRender();
    if (sections.length) {
      formData.append('sections', sections.join(','));
      formData.append('sections_url', window.location.pathname);
    }

    const config = fetchConfig('json', { body: formData });
    delete config.headers['Content-Type'];

    fetch(Theme.routes.cart_add_url, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.#showError(response.description || response.message);
          this.dispatchEvent(
            new CartErrorEvent(
              this.dataset.sectionId ?? '',
              response.message,
              response.description,
              response.errors
            )
          );
          return;
        }

        document.dispatchEvent(
          new CartAddEvent(response, this.dataset.sectionId ?? '', {
            source: 'product-form-component',
            itemCount: response.quantity ?? 1,
            variantId: String(response.id ?? ''),
            productId: this.productId,
            sections: response.sections,
          })
        );

        if (isBuyNow) {
          window.location.href = Theme.routes.cart_url;
          return;
        }

        this.querySelector('add-to-cart-component')?.playAddedAnimation?.();

        const cartDrawer = document.querySelector('cart-drawer-component');
        if (cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        }
      })
      .catch((error) => {
        console.error(error);
        this.#showError();
      })
      .finally(() => {
        submitButton?.removeAttribute('aria-disabled');
        submitButton?.classList.remove('loading');
      });
  }

  /**
   * @returns {string[]}
   */
  #sectionsToRender() {
    const sections = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((element) => {
      if (element instanceof HTMLElement && element.dataset.sectionId) {
        sections.add(element.dataset.sectionId);
      }
    });
    return Array.from(sections);
  }

  /**
   * @param {string} [message]
   */
  #showError(message) {
    const error = this.querySelector('[ref="addToCartTextError"]');
    if (error instanceof HTMLElement) error.classList.remove('hidden');

    const liveRegion = this.refs.liveRegion;
    if (liveRegion instanceof HTMLElement) liveRegion.textContent = message || '';
  }

  #clearError() {
    const error = this.querySelector('[ref="addToCartTextError"]');
    if (error instanceof HTMLElement) error.classList.add('hidden');
  }
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
